import { rem } from 'polished'
import React, { useContext } from 'react'
import styled from 'styled-components'

import FlightItemCardContent from './FlightItemCardContent'
import LEBookingTag from '../../../Tripkit/Common/LEBookingTag'
import DeepLinkButtons from '../Common/DeepLinkButtons'
import { ItemCardVariant } from '../ItemCard'

import NestableClickable from 'components/Common/NestableClickable'
import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import TextButton from 'components/Luxkit/Button/TextButton'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Group from 'components/utils/Group'
import PrintOnly from 'components/utils/PrintOnly'
import noop from 'lib/function/noop'
import { SAVE_CURATED_TRIP_BUTTON_CLASS } from 'tripPlanner/components/Buttons/SaveCuratedTripItemButton'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import TripContext from 'tripPlanner/contexts/TripContext'
import { FlightItem } from 'tripPlanner/types/tripItem'
import { isLeBooking, isLeBookmark } from 'tripPlanner/utils'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'

interface Props {
  className?: string
  item: FlightItem
  onClick?: React.MouseEventHandler<HTMLElement>
  onClickTripName?: () => void
  variant: ItemCardVariant
}

const Card = styled(NestableClickable)`
  display: grid;
  border-radius: ${(props) => props.theme.borderRadius.large};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(16)};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  break-inside: avoid-page;

  & > .${SAVE_CURATED_TRIP_BUTTON_CLASS} {
    justify-self: end;
    width: fit-content;
  }

  ${cardItemHoverAnimation}
`

function FlightItemCard({
  item,
  onClick,
  className,
  variant,
  onClickTripName = noop,
}: Props) {
  const tripContext = useContext(TripContext)
  const isLEBookingItem = isLeBooking(item)
  const isAddedToTripVisible = variant === 'drawer' && tripContext
  return (
    <Card onClick={onClick} className={className} role="button">
      <FlightItemCardContent item={item} />

      <Group
        direction={
          variant === 'drawer' && isLEBookingItem
            ? 'horizontal-reverse'
            : 'horizontal'
        }
        verticalAlign="center"
        horizontalAlign="space-between"
        gap={8}
      >
        {isLEBookingItem && <LEBookingTag />}
        {isLEBookingItem && isAddedToTripVisible && (
          <StructureLabel variant="information">
            <BodyTextBlock variant="small">Added to </BodyTextBlock>
            <TextLink
              size="caption"
              onClick={(e) => {
                e.stopPropagation()
                onClickTripName()
              }}
            >
              {tripContext.name}
            </TextLink>
          </StructureLabel>
        )}
      </Group>
      {isLeBookmark(item) && (
        <OnlyIfChildren
          as={Group}
          direction="horizontal"
          horizontalAlign={isAddedToTripVisible ? 'space-between' : 'end'}
          verticalAlign="center"
        >
          {isAddedToTripVisible && (
            <StructureLabel variant="information">
              <BodyTextBlock variant="small">Added to </BodyTextBlock>
              <TextLink
                size="caption"
                onClick={(e) => {
                  e.stopPropagation()
                  onClickTripName()
                }}
              >
                {tripContext.name}
              </TextLink>
            </StructureLabel>
          )}
          {!item.startDate && !item.endDate && (
            <Group
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="space-between"
              gap={12}
            >
              <SaveTripItemButton
                className={SAVE_CURATED_TRIP_BUTTON_CLASS}
                item={item}
              />
              <TextButton
                kind={variant === 'drawer' ? 'primary' : 'secondary'}
                nonInteractable
              >
                Search flights
              </TextButton>
            </Group>
          )}
          {item.startDate && item.endDate && item.savedItemData && (
            <Group
              direction="horizontal"
              verticalAlign="center"
              horizontalAlign="space-between"
              gap={12}
            >
              <SaveTripItemButton
                className={SAVE_CURATED_TRIP_BUTTON_CLASS}
                item={item}
              />
              <DeepLinkButtons item={item} variant={variant} />
            </Group>
          )}
        </OnlyIfChildren>
      )}

      {item.notes && (
        <PrintOnly>
          <BodyText variant="small" weight="normal" wrap="pre-wrap">
            {item.notes}
          </BodyText>
        </PrintOnly>
      )}
    </Card>
  )
}

export default FlightItemCard
